import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Typography,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  TableSortLabel,
  Chip,
  Tooltip,
  Alert,
  Link,
  LinearProgress,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon, Add, Visibility, PendingActions, InfoOutlined, FilterList as FilterListIcon, MoreTime as MoreTimeIcon, Link as LinkIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import { formatDateWithTimezone } from '../../../utils/timezoneUtils';
import { getWorkOrderDisplayNumber } from '../../../utils/workOrderDisplayNumber';
import {
  getStatusColor,
  getPriorityColor,
  getDaysUntilDue,
  getUrgencyColor,
  matchesAmpScheduleFilter,
  matchesUpcomingSourceFilter,
  matchesAdsbBulletinTypeFilter,
  isAdsbBulletinNotApplicable,
  isAmpAssemblyNotApplicable,
  SCHEDULE_FILTER_LABELS,
  BULLETIN_FILTER_LABELS,
  UPCOMING_SOURCE_FILTER_LABELS,
  WO_DETAIL_TABS
} from '../utils/maintenanceUtils';
import { useToast } from '../../../contexts/ToastContext';

const TYPE_LABELS = {
  INSPECTION: 'Inspection',
  MAINTENANCE: 'Maintenance',
  OVERHAUL: 'TSO (Time Since Overhaul)',
  REPAIR: 'Defect',
  REPLACEMENT: 'Replacement',
  TSN: 'TSN (Time Since New)',
  TSI: 'TSI (Time Since Inspection)',
  TSP: 'TSP (Time Since Part)',
  SLL: 'SLL (Service Life Limit)',
  OTL: 'OTL (Operational Time Limit)'
};

const getTypeLabel = (type) => TYPE_LABELS[type] || type;

const getComponentNameDisplay = (item) =>
  (item?.componentName || item?.componentDescription || '').trim();

const UNIT_COLUMN_DEFS = [
  { id: 'remainingDays', label: 'Days', enabledKey: 'days', unitKey: 'days' },
  { id: 'remainingHours', label: 'Hours', enabledKey: 'hours', unitKey: 'hours' },
  { id: 'remainingCycles', label: 'Cycles', enabledKey: 'cycles', unitKey: 'cycles' },
  { id: 'remainingLandings', label: 'Landings', enabledKey: 'landings', unitKey: 'landings' }
];

const BASE_SORTABLE_COLUMNS_BEFORE_UNITS = [
  { id: 'source', label: 'Source' },
  { id: 'type', label: 'Type' },
  { id: 'title', label: 'Title/Description' },
  { id: 'aircraft', label: 'Aircraft' },
  { id: 'componentName', label: 'Component Name' },
  { id: 'componentAssembly', label: 'Component/Assembly' },
  { id: 'lastPerformed', label: 'Last Performed' },
  { id: 'frequency', label: 'Frequency' },
  { id: 'dueDate', label: 'Due At' }
];

const BASE_SORTABLE_COLUMNS_AFTER_UNITS = [
  { id: 'priority', label: 'Priority' },
  { id: 'status', label: 'Status' }
];

function getUpcomingSortableColumns(rangeEnabled = {}) {
  const unitCols = UNIT_COLUMN_DEFS.filter((col) => !!rangeEnabled[col.enabledKey]);
  return [
    ...BASE_SORTABLE_COLUMNS_BEFORE_UNITS,
    ...unitCols,
    ...BASE_SORTABLE_COLUMNS_AFTER_UNITS
  ];
}

function getRemainingByUnitValue(item, unitKey) {
  const rbu = item?.remainingByUnit;
  if (!rbu || typeof rbu !== 'object') return null;
  const v = rbu[unitKey];
  return v != null && Number.isFinite(Number(v)) ? Number(v) : null;
}

function formatRemainingHoursDisplay(decimalHours) {
  if (decimalHours == null || !Number.isFinite(Number(decimalHours))) return null;
  const n = Number(decimalHours);
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const h = Math.floor(abs);
  let m = Math.round((abs - h) * 60);
  if (m >= 60) m = 59;
  return `${sign}${h}h ${String(m).padStart(2, '0')}m`;
}

function formatRemainingUnitCell(unitKey, value) {
  if (value == null) return null;
  if (unitKey === 'hours') return formatRemainingHoursDisplay(value);
  if (unitKey === 'days') {
    return value < 0 ? `Overdue ${Math.abs(value)}d` : `${value} days`;
  }
  if (unitKey === 'cycles') return `${value} cycles`;
  if (unitKey === 'landings') return `${value} landings`;
  return String(value);
}

function getRemainingUnitChipColor(unitKey, value) {
  if (value == null) return 'default';
  if (unitKey === 'days') return getUrgencyColor(value);
  if (value < 0) return 'error';
  if (unitKey === 'hours' && value <= 25) return 'warning';
  if (unitKey === 'cycles' && value <= 50) return 'warning';
  if (unitKey === 'landings' && value <= 25) return 'warning';
  return 'default';
}

function getUpcomingComparator(order, orderBy) {
  return (a, b) => {
    const getValue = (item, col) => {
      switch (col) {
        case 'source':
          return (item.source || '').toLowerCase();
        case 'type':
          return (item.type || '').toLowerCase();
        case 'title':
          return (item.title || '').toLowerCase();
        case 'aircraft':
          return (item.aircraftRegistration || 'N/A').toLowerCase();
        case 'componentName':
          return getComponentNameDisplay(item).toLowerCase();
        case 'componentAssembly':
          return (
            item.source === 'AMP_SCHEDULE'
              ? (item.ampAssemblyLabel || item.assemblyType || '')
              : (item.componentCode || item.assemblyType || '')
          ).toLowerCase();
        case 'lastPerformed':
          return (item.lastPerformed || '').toLowerCase();
        case 'frequency':
          return (item.frequency || '').toLowerCase();
        case 'dueDate':
          return item.effectiveDueDate || item.dueDate
            ? new Date(item.effectiveDueDate || item.dueDate).getTime()
            : null;
        case 'remainingDays':
          return getRemainingByUnitValue(item, 'days');
        case 'remainingHours':
          return getRemainingByUnitValue(item, 'hours');
        case 'remainingCycles':
          return getRemainingByUnitValue(item, 'cycles');
        case 'remainingLandings':
          return getRemainingByUnitValue(item, 'landings');
        case 'daysUntilDue':
          // Legacy sort key — prefer typed days, then remainingSortValue, then due date.
          {
            const days = getRemainingByUnitValue(item, 'days');
            if (days != null) return days;
            if (
              (item.source === 'AMP_SCHEDULE' ||
                item.source === 'ADSB_SERVICE_BULLETIN' ||
                item.source === 'COMPONENT_TIME_SINCE') &&
              item.remainingSortValue != null
            ) {
              return item.remainingSortValue;
            }
            return item.dueDate ? getDaysUntilDue(item.dueDate) : null;
          }
        case 'priority':
          return (item.priority || '').toLowerCase();
        case 'status':
          return (item.status || '').toLowerCase();
        default:
          return '';
      }
    };
    const aVal = getValue(a, orderBy);
    const bVal = getValue(b, orderBy);
    const aNull = aVal == null || aVal === '';
    const bNull = bVal == null || bVal === '';
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return order === 'asc' ? aVal - bVal : bVal - aVal;
    }
    const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
    return order === 'asc' ? cmp : -cmp;
  };
}

/** Source links for the "where is this from?" note. Each link goes to the page where that type is created/managed. */
const UPCOMING_SOURCES = [
  { label: 'Work orders', path: '/maintenance', description: 'Scheduled maintenance work orders' },
  { label: 'Component inspections', path: '/aircraft-lifecycle', description: 'Aircraft Life-cycle → Components → Inspections' },
  { label: 'Component services', path: '/aircraft-lifecycle', description: 'Aircraft Life-cycle → Components → Services' },
  { label: 'Assembly inspections', path: '/aircraft-lifecycle', description: 'Aircraft Life-cycle → Assemblies' },
  { label: 'Assembly services', path: '/aircraft-lifecycle', description: 'Aircraft Life-cycle → Assemblies' },
  { label: 'Time Since Records (Assembly & Component)', path: '/aircraft-lifecycle', description: 'SLL, TSN, TSO, etc. with limits in Assemblies & Components' },
  { label: 'AMP Schedules', path: '/amp-schedules', description: 'Aircraft Maintenance Program schedules' },
  { label: 'AD/SB Service Bulletins', path: '/adsb-service-bulletin', description: 'Airworthiness Directives and Service Bulletins' }
];

/** One table cell with multiple metric rows (native units — same strings as AMP Schedules page). */
function StackedMetricLines({ lines, displayTimezone, mode = 'default' }) {
  if (!lines || lines.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
      {lines.map((line, i) => {
        let displayText = line.text;
        if (mode === 'due') {
          if (line.nextDueDateLow && line.nextDueDateHigh) {
            const low = displayTimezone ? formatDateWithTimezone(line.nextDueDateLow, displayTimezone) : line.nextDueDateLow;
            const high = displayTimezone ? formatDateWithTimezone(line.nextDueDateHigh, displayTimezone) : line.nextDueDateHigh;
            displayText = `Between ${low} - ${high}`;
          } else if (line.nextDueDate) {
            displayText = displayTimezone ? formatDateWithTimezone(line.nextDueDate, displayTimezone) : line.nextDueDate;
          }
        }

        if (line.tooltip) {
          return (
            <Tooltip key={i} title={line.tooltip} arrow>
              <Box component="span" sx={{ display: 'block', cursor: 'help' }}>
                <Typography variant="body2" component="span" sx={{ lineHeight: 1.4 }}>
                  {displayText}
                </Typography>
              </Box>
            </Tooltip>
          );
        }

        return (
          <Typography key={i} variant="body2" component="div" sx={{ lineHeight: 1.4 }}>
            {displayText}
          </Typography>
        );
      })}
    </Box>
  );
}

/**
 * Upcoming Maintenance Tab Component
 */
const UpcomingMaintenanceTab = ({
  upcomingMaintenance,
  loadingUpcoming,
  refreshingUpcoming,
  upcomingDays,
  setUpcomingDays,
  customDays,
  setCustomDays,
  upcomingHours,
  setUpcomingHours,
  customHours,
  setCustomHours,
  upcomingCycles,
  setUpcomingCycles,
  customCycles,
  setCustomCycles,
  upcomingLandings,
  setUpcomingLandings,
  customLandings,
  setCustomLandings,
  upcomingRangeEnabled,
  setUpcomingRangeEnabled,
  upcomingAircraftFilter,
  setUpcomingAircraftFilter,
  upcomingTypeFilter,
  setUpcomingTypeFilter,
  upcomingScheduleFilter,
  setUpcomingScheduleFilter,
  upcomingSourceFilter,
  setUpcomingSourceFilter,
  upcomingAdsbBulletinTypeFilter,
  setUpcomingAdsbBulletinTypeFilter,
  aircraft,
  canManageMaintenance,
  canCreateIntimation,
  canLinkIntimationSource,
  setSelectedMaintenance,
  setDetailOpen,
  setDetailTab,
  setFormData,
  setOpen,
  onCreateIntimation,
  onLinkExistingIntimation,
  onViewIntimation,
  onExtendDue,
  canRequestDueExtension,
  canApproveDueExtension,
  onFilteredUpcomingChange,
  onFilter,
  canFilter,
  showToast,
  displayTimezone
}) => {
  const navigate = useNavigate();
  const [upcomingSearchTerm, setUpcomingSearchTerm] = useState('');
  const [dueWindowFilter, setDueWindowFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('dueDate');
  const [order, setOrder] = useState('asc');

  const sortableColumns = React.useMemo(
    () => getUpcomingSortableColumns(upcomingRangeEnabled),
    [upcomingRangeEnabled]
  );

  const enabledUnitColumns = React.useMemo(
    () => UNIT_COLUMN_DEFS.filter((col) => !!upcomingRangeEnabled[col.enabledKey]),
    [upcomingRangeEnabled]
  );

  // If the active sort column is a unit column that was turned off, fall back to Due At.
  useEffect(() => {
    const stillVisible = sortableColumns.some((col) => col.id === orderBy);
    if (!stillVisible) {
      setOrderBy('dueDate');
      setOrder('asc');
    }
  }, [sortableColumns, orderBy]);

  // Reset to first page when filtered list length changes (e.g. filters/search)
  useEffect(() => {
    setPage(0);
  }, [
    upcomingMaintenance.length,
    upcomingSearchTerm,
    upcomingAircraftFilter,
    upcomingTypeFilter,
    upcomingScheduleFilter,
    upcomingSourceFilter,
    upcomingAdsbBulletinTypeFilter,
    dueWindowFilter,
    upcomingRangeEnabled
  ]);

  const handleRequestSort = (_, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const toggleRangeEnabled = (key) => {
    setUpcomingRangeEnabled((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCustomDaysChange = (value) => {
    const days = parseInt(value, 10);
    if (!isNaN(days) && days > 0) {
      setUpcomingDays(days);
      setCustomDays('');
    }
  };

  const handleDaysDropdownChange = (value) => {
    if (value === 'custom') {
      setUpcomingDays('custom');
    } else {
      setUpcomingDays(value);
      setCustomDays('');
    }
  };

  const handleCustomHoursChange = (value) => {
    const hours = parseInt(value, 10);
    if (!isNaN(hours) && hours > 0) {
      setUpcomingHours(hours);
      setCustomHours('');
    }
  };

  const handleHoursDropdownChange = (value) => {
    if (value === 'custom') {
      setUpcomingHours('custom');
    } else {
      setUpcomingHours(value);
      setCustomHours('');
    }
  };

  const handleCustomCyclesChange = (value) => {
    const c = parseInt(value, 10);
    if (!isNaN(c) && c > 0) {
      setUpcomingCycles(c);
      setCustomCycles('');
    }
  };

  const handleCyclesDropdownChange = (value) => {
    if (value === 'custom') {
      setUpcomingCycles('custom');
    } else {
      setUpcomingCycles(value);
      setCustomCycles('');
    }
  };

  const handleCustomLandingsChange = (value) => {
    const n = parseInt(value, 10);
    if (!isNaN(n) && n > 0) {
      setUpcomingLandings(n);
      setCustomLandings('');
    }
  };

  const handleLandingsDropdownChange = (value) => {
    if (value === 'custom') {
      setUpcomingLandings('custom');
    } else {
      setUpcomingLandings(value);
      setCustomLandings('');
    }
  };

  const activeRangeLabelParts = [];
  if (upcomingRangeEnabled.days && typeof upcomingDays === 'number') {
    activeRangeLabelParts.push(`${upcomingDays} days`);
  }
  if (upcomingRangeEnabled.hours && typeof upcomingHours === 'number') {
    activeRangeLabelParts.push(`${upcomingHours} hours`);
  }
  if (upcomingRangeEnabled.cycles && typeof upcomingCycles === 'number') {
    activeRangeLabelParts.push(`${upcomingCycles} cycles`);
  }
  if (upcomingRangeEnabled.landings && typeof upcomingLandings === 'number') {
    activeRangeLabelParts.push(`${upcomingLandings} landings`);
  }
  const activeRangeLabel =
    activeRangeLabelParts.length > 0
      ? `Active ranges: ${activeRangeLabelParts.join(' + ')}`
      : 'No range filter — showing all rows. Enable Days/Hours/Cycles/Landings and click Filter to narrow.';

  const handleSourceFilterChange = (value) => {
    setUpcomingSourceFilter(value);
    if (value !== 'ADSB_SERVICE_BULLETIN') {
      setUpcomingAdsbBulletinTypeFilter('ALL');
    }
  };

  // Filter upcoming maintenance (memoized so sorted list + parent export state stay stable between renders)
  const filteredUpcoming = React.useMemo(
    () =>
      upcomingMaintenance.filter((item) => {
        const searchLower = upcomingSearchTerm.toLowerCase();
        const matchesSearch =
          !upcomingSearchTerm ||
          item.title?.toLowerCase().includes(searchLower) ||
          item.aircraftRegistration?.toLowerCase().includes(searchLower) ||
          item.componentCode?.toLowerCase().includes(searchLower) ||
          item.componentDescription?.toLowerCase().includes(searchLower) ||
          getComponentNameDisplay(item).toLowerCase().includes(searchLower) ||
          (item.adsbBulletinNumber && item.adsbBulletinNumber.toLowerCase().includes(searchLower));

        let matchesDueWindow = true;
        if (upcomingRangeEnabled.days && dueWindowFilter !== 'ALL') {
          const isOverdueByLiveRemaining =
            (item.source === 'AMP_SCHEDULE' ||
              item.source === 'ADSB_SERVICE_BULLETIN' ||
              item.source === 'COMPONENT_TIME_SINCE') &&
            item.remainingSortValue != null &&
            item.remainingSortValue < 0;
          if (!item.dueDate) {
            matchesDueWindow = dueWindowFilter === 'PAST_DUE_ONLY' && isOverdueByLiveRemaining;
          } else {
            const daysUntilDue = getDaysUntilDue(item.dueDate);
            matchesDueWindow =
              dueWindowFilter === 'PAST_DUE_ONLY'
                ? daysUntilDue < 0 || isOverdueByLiveRemaining
                : daysUntilDue >= 0;
          }
        }

        // AD/SB with Bulletin Applicable = false never belong in upcoming maintenance.
        if (isAdsbBulletinNotApplicable(item)) return false;

        const matchesScheduleFilter = matchesAmpScheduleFilter(item, upcomingScheduleFilter);
        const matchesSourceFilter = matchesUpcomingSourceFilter(item, upcomingSourceFilter);
        const matchesBulletinTypeFilter =
          upcomingSourceFilter === 'ADSB_SERVICE_BULLETIN'
            ? matchesAdsbBulletinTypeFilter(item, upcomingAdsbBulletinTypeFilter)
            : true;
        const matchesScheduleFilterForRow =
          upcomingSourceFilter === 'ADSB_SERVICE_BULLETIN' ? true : matchesScheduleFilter;

        return (
          matchesSearch &&
          matchesDueWindow &&
          matchesScheduleFilterForRow &&
          matchesSourceFilter &&
          matchesBulletinTypeFilter
        );
      }),
    [
      upcomingMaintenance,
      upcomingSearchTerm,
      upcomingRangeEnabled,
      dueWindowFilter,
      upcomingScheduleFilter,
      upcomingSourceFilter,
      upcomingAdsbBulletinTypeFilter
    ]
  );

  const sortedUpcoming = React.useMemo(() => {
    return [...filteredUpcoming].sort(getUpcomingComparator(order, orderBy));
  }, [filteredUpcoming, order, orderBy]);

  useEffect(() => {
    if (onFilteredUpcomingChange) {
      onFilteredUpcomingChange(sortedUpcoming);
    }
  }, [sortedUpcoming, onFilteredUpcomingChange]);

  const paginatedItems = sortedUpcoming.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const showInitialLoading = loadingUpcoming && upcomingMaintenance.length === 0;

  return (
    <>
      {/* Date Range and Filters Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={2}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!upcomingRangeEnabled.days}
                  onChange={() => toggleRangeEnabled('days')}
                />
              }
              label="Days"
              sx={{ mb: 0.5, ml: 0 }}
            />
            <FormControl fullWidth size="small" disabled={!upcomingRangeEnabled.days}>
              <InputLabel>Show Next (Days)</InputLabel>
              <Select
                value={typeof upcomingDays === 'number' ? upcomingDays : (upcomingDays === 'custom' ? 'custom' : 90)}
                label="Show Next (Days)"
                onChange={(e) => handleDaysDropdownChange(e.target.value)}
              >
                <MenuItem value={30}>30 Days</MenuItem>
                <MenuItem value={60}>60 Days</MenuItem>
                <MenuItem value={90}>90 Days</MenuItem>
                <MenuItem value={120}>120 Days</MenuItem>
                <MenuItem value={180}>180 Days</MenuItem>
                <MenuItem value={240}>240 Days</MenuItem>
                <MenuItem value={480}>480 Days</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
            {upcomingRangeEnabled.days && upcomingDays === 'custom' && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter days"
                  value={customDays}
                  onChange={(e) => setCustomDays(e.target.value)}
                  type="number"
                  InputProps={{
                    inputProps: { min: 1 }
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleCustomDaysChange(customDays)}
                  disabled={!customDays || parseInt(customDays, 10) <= 0}
                >
                  Apply
                </Button>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!upcomingRangeEnabled.hours}
                  onChange={() => toggleRangeEnabled('hours')}
                />
              }
              label="Hours"
              sx={{ mb: 0.5, ml: 0 }}
            />
            <FormControl fullWidth size="small" disabled={!upcomingRangeEnabled.hours}>
              <InputLabel>Show Next (Hours)</InputLabel>
              <Select
                value={typeof upcomingHours === 'number' ? upcomingHours : (upcomingHours === 'custom' ? 'custom' : 100)}
                label="Show Next (Hours)"
                onChange={(e) => handleHoursDropdownChange(e.target.value)}
              >
                <MenuItem value={25}>25 Hours</MenuItem>
                <MenuItem value={50}>50 Hours</MenuItem>
                <MenuItem value={100}>100 Hours</MenuItem>
                <MenuItem value={250}>250 Hours</MenuItem>
                <MenuItem value={500}>500 Hours</MenuItem>
                <MenuItem value={1000}>1000 Hours</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
            {upcomingRangeEnabled.hours && upcomingHours === 'custom' && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter hours"
                  value={customHours}
                  onChange={(e) => setCustomHours(e.target.value)}
                  type="number"
                  InputProps={{
                    inputProps: { min: 1 }
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleCustomHoursChange(customHours)}
                  disabled={!customHours || parseInt(customHours, 10) <= 0}
                >
                  Apply
                </Button>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!upcomingRangeEnabled.cycles}
                  onChange={() => toggleRangeEnabled('cycles')}
                />
              }
              label="Cycles"
              sx={{ mb: 0.5, ml: 0 }}
            />
            <FormControl fullWidth size="small" disabled={!upcomingRangeEnabled.cycles}>
              <InputLabel>Show Next (Cycles)</InputLabel>
              <Select
                value={
                  typeof upcomingCycles === 'number'
                    ? upcomingCycles
                    : upcomingCycles === 'custom'
                      ? 'custom'
                      : 500
                }
                label="Show Next (Cycles)"
                onChange={(e) => handleCyclesDropdownChange(e.target.value)}
              >
                <MenuItem value={100}>100 Cycles</MenuItem>
                <MenuItem value={250}>250 Cycles</MenuItem>
                <MenuItem value={500}>500 Cycles</MenuItem>
                <MenuItem value={1000}>1000 Cycles</MenuItem>
                <MenuItem value={2500}>2500 Cycles</MenuItem>
                <MenuItem value={5000}>5000 Cycles</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
            {upcomingRangeEnabled.cycles && upcomingCycles === 'custom' && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter cycles"
                  value={customCycles}
                  onChange={(e) => setCustomCycles(e.target.value)}
                  type="number"
                  InputProps={{
                    inputProps: { min: 1 }
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleCustomCyclesChange(customCycles)}
                  disabled={!customCycles || parseInt(customCycles, 10) <= 0}
                >
                  Apply
                </Button>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!upcomingRangeEnabled.landings}
                  onChange={() => toggleRangeEnabled('landings')}
                />
              }
              label="Landings"
              sx={{ mb: 0.5, ml: 0 }}
            />
            <FormControl fullWidth size="small" disabled={!upcomingRangeEnabled.landings}>
              <InputLabel>Show Next (Landings)</InputLabel>
              <Select
                value={
                  typeof upcomingLandings === 'number'
                    ? upcomingLandings
                    : upcomingLandings === 'custom'
                      ? 'custom'
                      : 250
                }
                label="Show Next (Landings)"
                onChange={(e) => handleLandingsDropdownChange(e.target.value)}
              >
                <MenuItem value={50}>50 Landings</MenuItem>
                <MenuItem value={100}>100 Landings</MenuItem>
                <MenuItem value={250}>250 Landings</MenuItem>
                <MenuItem value={500}>500 Landings</MenuItem>
                <MenuItem value={1000}>1000 Landings</MenuItem>
                <MenuItem value={2500}>2500 Landings</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
            {upcomingRangeEnabled.landings && upcomingLandings === 'custom' && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter landings"
                  value={customLandings}
                  onChange={(e) => setCustomLandings(e.target.value)}
                  type="number"
                  InputProps={{
                    inputProps: { min: 1 }
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleCustomLandingsChange(customLandings)}
                  disabled={!customLandings || parseInt(customLandings, 10) <= 0}
                >
                  Apply
                </Button>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Aircraft</InputLabel>
              <Select
                value={upcomingAircraftFilter}
                label="Aircraft"
                onChange={(e) => setUpcomingAircraftFilter(e.target.value)}
              >
                <MenuItem value="ALL">All Aircraft</MenuItem>
                {aircraft.map((ac) => (
                  <MenuItem key={ac._id} value={ac._id}>
                    {ac.registration}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={upcomingTypeFilter}
                label="Type"
                onChange={(e) => setUpcomingTypeFilter(e.target.value)}
              >
                <MenuItem value="ALL">All Types</MenuItem>
                <MenuItem value="INSPECTION">Inspection</MenuItem>
                <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
                <MenuItem value="OVERHAUL">TSO (Time Since Overhaul)</MenuItem>
                <MenuItem value="REPAIR">Defect</MenuItem>
                <MenuItem value="REPLACEMENT">Replacement</MenuItem>
                <MenuItem value="TSN">TSN (Time Since New)</MenuItem>
                <MenuItem value="TSI">TSI (Time Since Inspection)</MenuItem>
                <MenuItem value="TSP">TSP (Time Since Part)</MenuItem>
                <MenuItem value="SLL">SLL (Service Life Limit)</MenuItem>
                <MenuItem value="OTL">OTL (Operational Time Limit)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Source</InputLabel>
              <Select
                value={upcomingSourceFilter}
                label="Source"
                onChange={(e) => handleSourceFilterChange(e.target.value)}
                renderValue={(v) => UPCOMING_SOURCE_FILTER_LABELS[v] || v}
              >
                {Object.entries(UPCOMING_SOURCE_FILTER_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {upcomingSourceFilter === 'ADSB_SERVICE_BULLETIN' && (
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Bulletin filter</InputLabel>
                <Select
                  value={upcomingAdsbBulletinTypeFilter}
                  label="Bulletin filter"
                  onChange={(e) => setUpcomingAdsbBulletinTypeFilter(e.target.value)}
                  renderValue={(v) => BULLETIN_FILTER_LABELS[v] || v}
                >
                  {Object.entries(BULLETIN_FILTER_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search..."
              value={upcomingSearchTerm}
              onChange={(e) => setUpcomingSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: upcomingSearchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setUpcomingSearchTerm('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          {upcomingSourceFilter !== 'ADSB_SERVICE_BULLETIN' && (
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Schedule filter</InputLabel>
                <Select
                  value={upcomingScheduleFilter}
                  label="Schedule filter"
                  onChange={(e) => setUpcomingScheduleFilter(e.target.value)}
                  renderValue={(v) => SCHEDULE_FILTER_LABELS[v] || v}
                >
                  <MenuItem value="ALL">All</MenuItem>
                  <MenuItem value="NOT_APPLICABLE">Not Applicable</MenuItem>
                  <MenuItem value="EXCLUDE_NOT_APPLICABLE">Exclude Not Applicable</MenuItem>
                  <MenuItem value="ONE_TIME">One time</MenuItem>
                  <MenuItem value="EXCLUDE_ONE_TIME">Exclude one time</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Due Window</InputLabel>
              <Select
                value={dueWindowFilter}
                label="Due Window"
                onChange={(e) => setDueWindowFilter(e.target.value)}
                disabled={!upcomingRangeEnabled.days}
              >
                <MenuItem value="ALL">All (Past Due + Upcoming)</MenuItem>
                <MenuItem value="PAST_DUE_ONLY">Past Due Only</MenuItem>
                <MenuItem value="UPCOMING_ONLY">Upcoming Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              size="small"
              startIcon={<FilterListIcon />}
              onClick={onFilter}
              disabled={!canFilter || refreshingUpcoming}
              sx={{ height: 40 }}
            >
              Filter
            </Button>
          </Grid>
        </Grid>
        {!showInitialLoading && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              color={upcomingRangeEnabled.days ? 'primary' : 'secondary'}
              variant="outlined"
              label={activeRangeLabel}
            />
            <Typography variant="body2" color="text.secondary">
              {refreshingUpcoming
                ? 'Updating list…'
                : `Showing ${filteredUpcoming.length} maintenance items`}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Info: where upcoming items come from and how to manage them */}
      <Alert
        severity="info"
        icon={<InfoOutlined />}
        sx={{ mb: 2 }}
      >
        <Typography variant="subtitle2" color="text.primary" gutterBottom>
          Upcoming items are built from the following sources. Use the links to open each area and see how items are created or managed.
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
          {UPCOMING_SOURCES.map(({ label, path, description }) => (
            <li key={label}>
              <Link
                component={RouterLink}
                to={path}
                underline="hover"
                color="primary"
              >
                {label}
              </Link>
              {' — '}
              <Typography component="span" variant="body2" color="text.secondary">
                {description}
              </Typography>
            </li>
          ))}
        </Box>
      </Alert>

      {/* Upcoming Maintenance Table */}
      {showInitialLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <Typography>Loading upcoming maintenance...</Typography>
        </Box>
      ) : (
        <Box sx={{ position: 'relative' }}>
          {refreshingUpcoming && (
            <LinearProgress
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 2,
                borderRadius: '4px 4px 0 0'
              }}
            />
          )}
          <TableContainer
            component={Paper}
            sx={{
              opacity: refreshingUpcoming ? 0.72 : 1,
              transition: 'opacity 0.15s ease-in-out',
              pointerEvents: refreshingUpcoming ? 'none' : 'auto'
            }}
          >
          <Table>
            <TableHead>
              <TableRow>
                {sortableColumns.map((col) => (
                  <TableCell key={col.id} sortDirection={orderBy === col.id ? order : false}>
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? order : 'asc'}
                      onClick={(e) => handleRequestSort(e, col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUpcoming.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={sortableColumns.length + 1} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {upcomingSearchTerm ||
                      upcomingAircraftFilter !== 'ALL' ||
                      upcomingTypeFilter !== 'ALL' ||
                      upcomingSourceFilter !== 'ALL' ||
                      upcomingAdsbBulletinTypeFilter !== 'ALL' ||
                      upcomingScheduleFilter !== 'ALL'
                        ? 'No upcoming maintenance matches the current filters'
                        : 'No upcoming maintenance found for the selected period'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedItems.map((item, index) => {
                  const displayDueDate = item.effectiveDueDate || item.dueDate;
                  const extendedDueTooltip = item.isExtended
                    ? `Extended due (baseline: ${
                        item.baselineDueDate
                          ? (displayTimezone
                              ? formatDateWithTimezone(item.baselineDueDate, displayTimezone)
                              : new Date(item.baselineDueDate).toLocaleDateString())
                          : item.baselineDueAtLimit
                            ? `${item.baselineDueAtLimit.value} ${item.baselineDueAtLimit.unit}`
                            : item.baselineDueByRemaining
                              ? `${item.baselineDueByRemaining.value} ${item.baselineDueByRemaining.unit}`
                              : 'see source'
                      })`
                    : null;
                  const sourceLabel = item.source === 'WORK_ORDER' ? 'Work Order' :
                    item.source === 'COMPONENT_INSPECTION' ? 'Inspection' :
                    item.source === 'COMPONENT_SERVICE' ? 'Service' :
                    item.source === 'ASSEMBLY_TIME_SINCE' ? 'Assembly TSR' :
                    item.source === 'COMPONENT_TIME_SINCE' ? 'Component TSR' :
                    item.source === 'AMP_SCHEDULE' ? 'AMP Schedule' :
                    item.source === 'ADSB_SERVICE_BULLETIN' ? 'AD/SB' : 'Other';
                  const dueAtLines =
                    item.source === 'AMP_SCHEDULE'
                      ? item.ampDueAtLines
                      : item.source === 'ADSB_SERVICE_BULLETIN'
                        ? item.adsbDueAtLines
                        : null;
                  const rowKey = `${item.source}-${item.sourceId}-${item.tsrIndex ?? ''}-${page * rowsPerPage + index}`;
                  return (
                    <TableRow key={rowKey}>
                      <TableCell>
                        <Chip
                          label={sourceLabel}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getTypeLabel(item.type)}
                          size="small"
                          color={item.type === 'INSPECTION' ? 'info' : 
                                 item.type === 'OVERHAUL' ? 'warning' : 
                                 item.type === 'REPAIR' ? 'error' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {item.title}
                        </Typography>
                        {item.description && item.description !== item.title && (
                          <Typography variant="caption" color="text.secondary">
                            {item.description}
                          </Typography>
                        )}
                        {(item.physicalWorkOrderNumber || item.workOrderNumber) && (
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
                            WO #{getWorkOrderDisplayNumber(item)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {item.aircraftRegistration || 'N/A'}
                        </Typography>
                        {item.aircraftModel && (
                          <Typography variant="caption" color="text.secondary">
                            {item.aircraftModel}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {getComponentNameDisplay(item) ? (
                          <Typography variant="body2">{getComponentNameDisplay(item)}</Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.source === 'AMP_SCHEDULE' ? (
                          isAmpAssemblyNotApplicable(item) ? (
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              NA
                            </Typography>
                          ) : item.ampAssemblyLabel ? (
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {item.ampAssemblyLabel}
                            </Typography>
                          ) : item.assemblyType ? (
                            <>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {item.assemblyType}
                              </Typography>
                              {item.assemblySerialNumber && (
                                <Typography variant="caption" color="text.secondary">
                                  S/N: {item.assemblySerialNumber}
                                </Typography>
                              )}
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              None
                            </Typography>
                          )
                        ) : item.componentCode ? (
                          <>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {item.componentCode}
                            </Typography>
                            {item.componentPartNumber && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                P/N: {item.componentPartNumber}
                              </Typography>
                            )}
                            {item.componentPosition && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Pos: {item.componentPosition}
                              </Typography>
                            )}
                            {item.assemblyType && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Assembly: {item.assemblyType} {item.assemblySerialNumber ? `(${item.assemblySerialNumber})` : ''}
                              </Typography>
                            )}
                          </>
                        ) : item.assemblyType ? (
                          <>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {item.assemblyType}
                            </Typography>
                            {item.assemblySerialNumber && (
                              <Typography variant="caption" color="text.secondary">
                                S/N: {item.assemblySerialNumber}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            N/A
                          </Typography>
                        )}
                        {item.ataChapter && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            ATA: {item.ataChapter} {item.ataChapterName ? `(${item.ataChapterName})` : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        {item.lastPerformed ? (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {item.lastPerformed}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        {item.frequency ? (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {item.frequency}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={extendedDueTooltip || ''} disableHoverListener={!extendedDueTooltip}>
                          <Box>
                            {Array.isArray(dueAtLines) ? (
                              <StackedMetricLines lines={dueAtLines} displayTimezone={displayTimezone} mode="due" />
                            ) : displayDueDate ? (
                              <Typography variant="body2" sx={item.isExtended ? { fontWeight: 'bold' } : undefined}>
                                {displayTimezone ? formatDateWithTimezone(displayDueDate, displayTimezone) : new Date(displayDueDate).toLocaleDateString()}
                              </Typography>
                            ) : item.dueAtLimit ? (
                              <Typography variant="body2" color="text.secondary" sx={item.isExtended ? { fontWeight: 'bold' } : undefined}>
                                {item.dueAtLimit.value} {item.dueAtLimit.unit}
                              </Typography>
                            ) : item.dueByRemaining ? (
                              <Typography variant="body2" color="text.secondary">
                                Due by remaining: {item.dueByRemaining.value} {item.dueByRemaining.unit}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Not set
                              </Typography>
                            )}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      {enabledUnitColumns.map((col) => {
                        const unitValue = getRemainingByUnitValue(item, col.unitKey);
                        const label = formatRemainingUnitCell(col.unitKey, unitValue);
                        return (
                          <TableCell key={col.id}>
                            <Tooltip title={extendedDueTooltip || ''} disableHoverListener={!extendedDueTooltip || unitValue == null}>
                              <Box>
                                {label != null ? (
                                  <Chip
                                    label={label}
                                    size="small"
                                    color={getRemainingUnitChipColor(col.unitKey, unitValue)}
                                    sx={item.isExtended ? { fontWeight: 'bold' } : undefined}
                                  />
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    —
                                  </Typography>
                                )}
                              </Box>
                            </Tooltip>
                          </TableCell>
                        );
                      })}
                      <TableCell>
                        <Chip
                          label={item.priority}
                          size="small"
                          color={getPriorityColor(item.priority)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.status}
                          size="small"
                          color={getStatusColor(item.status)}
                        />
                        {item.isExtended && (
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={`Extended${item.activeExtensions?.length > 1 ? ` (${item.activeExtensions.length})` : ''}`}
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          </Box>
                        )}
                        {item.intimationId && (
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={`Intimation: ${item.intimationStatus || 'PENDING'}`}
                              size="small"
                              color={item.intimationStatus === 'ACKNOWLEDGED' ? 'success' : item.intimationStatus === 'COMPLETED' ? 'default' : item.intimationStatus === 'CLARIFICATION_REQUESTED' ? 'warning' : item.intimationStatus === 'ARCHIVED' ? 'default' : 'info'}
                              variant="outlined"
                              clickable
                              onClick={() => {
                                const id = (item.intimationId && (item.intimationId._id || item.intimationId)).toString();
                                if (onViewIntimation) {
                                  onViewIntimation(id);
                                } else {
                                  navigate('/maintenance', { state: { openIntimationsTab: true, intimationId: id } });
                                }
                              }}
                              sx={{ mr: 0.5, cursor: 'pointer' }}
                            />
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() => {
                                const id = (item.intimationId && (item.intimationId._id || item.intimationId)).toString();
                                if (onViewIntimation) {
                                  onViewIntimation(id);
                                } else {
                                  navigate('/maintenance', { state: { openIntimationsTab: true, intimationId: id } });
                                }
                              }}
                            >
                              {String(item.intimationId && (item.intimationId._id || item.intimationId)).slice(-6)}
                            </Link>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {item.source === 'WORK_ORDER' && item.sourceId ? (
                            <Tooltip title="View Work Order">
                              <IconButton
                                size="small"
                                onClick={async () => {
                                  try {
                                    const response = await axios.get(`/api/maintenance/${item.sourceId}`);
                                    setSelectedMaintenance(response.data);
                                    setDetailOpen(true);
                                    setDetailTab(WO_DETAIL_TABS.OVERVIEW);
                                  } catch (error) {
                                    console.error('Error fetching work order:', error);
                                    showToast('Failed to fetch work order details', 'error');
                                  }
                                }}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          ) : item.source === 'ASSEMBLY_TIME_SINCE' && item.assemblyId ? (
                            <Tooltip title="View Assembly (Aircraft Life-cycle)">
                              <IconButton
                                size="small"
                                onClick={() => window.open(`/aircraft-lifecycle?assemblyId=${item.assemblyId}`, '_blank')}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          ) : (item.source === 'COMPONENT_TIME_SINCE' || item.componentId) ? (
                            <Tooltip title="View Component (Aircraft Life-cycle)">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (item.componentId) {
                                    window.open(`/aircraft-lifecycle?componentId=${item.componentId}`, '_blank');
                                  }
                                }}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          ) : item.source === 'AMP_SCHEDULE' && item.sourceId ? (
                            <Tooltip title="View AMP Schedule">
                              <IconButton
                                size="small"
                                onClick={() => window.open(`/amp-schedules?scheduleId=${item.sourceId}`, '_blank')}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          ) : item.source === 'ADSB_SERVICE_BULLETIN' && item.sourceId ? (
                            <Tooltip title="View AD/SB Service Bulletin">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  window.open(
                                    `/adsb-service-bulletin?bulletinId=${encodeURIComponent(String(item.sourceId))}`,
                                    '_blank'
                                  )
                                }
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="View Details">
                              <IconButton size="small" disabled>
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canManageMaintenance && item.source === 'WORK_ORDER' && (
                            <Tooltip title="Create Work Order">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => {
                                  setFormData({
                                    aircraftId: item.aircraftId || '',
                                    type: item.type || 'INSPECTION',
                                    title: item.title || '',
                                    description: item.description || '',
                                    priority: item.priority || 'MEDIUM',
                                    status: 'SCHEDULED',
                                    scheduledDate: item.dueDate ? dayjs(item.dueDate) : null,
                                    inspectionType: item.inspectionType || ''
                                  });
                                  setOpen(true);
                                }}
                              >
                                <Add />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canCreateIntimation(item) && onCreateIntimation && !item.intimationId && (
                            <Tooltip title="Create Intimation to Create Work Order">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => onCreateIntimation(item)}
                              >
                                <PendingActions />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canLinkIntimationSource && onLinkExistingIntimation && !item.intimationId && (
                            <Tooltip title="Link existing intimation (work order already started)">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => onLinkExistingIntimation(item)}
                              >
                                <LinkIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {onExtendDue &&
                            ((canRequestDueExtension && canRequestDueExtension(item)) ||
                              canApproveDueExtension) && (
                            <Tooltip title="Extend due (DGCA approval)">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => onExtendDue(item)}
                              >
                                <MoreTimeIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          {sortedUpcoming.length > 0 && (
            <TablePagination
              component="div"
              count={sortedUpcoming.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`}
            />
          )}
        </TableContainer>
        </Box>
      )}
    </>
  );
};

export default UpcomingMaintenanceTab;
